import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type ModalType, ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { type PageLayoutStore } from 'shared/layout'
import { ContactsApi } from 'entities/Contacts'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { type History } from 'pages/contacts/pages/history/model/History'
import { HistoryModalContent } from 'pages/contacts/pages/history/ui/HistoryModalContent'
import { ImportContactsStore } from 'pages/contacts/ui/ImportContacts'
import { ImportReasonsStore } from 'pages/contacts/pages/history/store/ImportReasonsStore'

type IIModalProps = {
  title: string
  width: number
  desc?: ModalType['desc']
  ModalContent?: ModalType['ModalContent']
}

export class HistoryPageStore {
  private readonly _importContactsStore: ImportContactsStore
  private readonly _importReasonsStore: ImportReasonsStore
  private _historiesMap: Map<number, History> = new Map()
  private _deletedHistoriesMap: Map<number, History> = new Map()
  private _modalId = ''
  private _scrollToTOpTrigger = ''
  private _disposeChangePagination: IReactionDisposer | null = null
  private readonly _contactsTagsModalStore: ContactsTagsModalStore

  constructor(private _pageLayoutStore: PageLayoutStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._importContactsStore = new ImportContactsStore(this._entities)
    this._importReasonsStore = new ImportReasonsStore()
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._importContactsStore.setConfig({
      addItem: (item: History) => {
        this.addHistory(item)
      },
      reset: () => {
        this.resetHistories()
      },
    })

    this.reactionChangePagination()
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get importContactsStore() {
    return this._importContactsStore
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get historyData() {
    return Array.from(this._historiesMap.values())
  }

  get contactsImportModuleStore() {
    return this._importContactsStore.contactsImportModuleStore
  }

  get totalHistory() {
    return this.contactsImportModuleStore.totalHistory
  }

  get pagination() {
    return this.contactsImportModuleStore.paginationData
  }

  get isLoading() {
    return this.contactsImportModuleStore.loading
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get isNoImportsYet() {
    return !this.isLoading && !this._historiesMap.size
  }

  get pendingId() {
    const pendingHistory = this.historyData.find(
      (history) => history.createdAt && history.finishedAt === null
    )

    return this.historyData.length === 1 && pendingHistory ? pendingHistory.id : null
  }

  checkIsExistHistory = (id: number) => {
    return this._historiesMap.has(id)
  }

  dispose = () => {
    this.contactsImportModuleStore.dispose()
    this._importReasonsStore.reset()
    this._historiesMap.clear()
    this._deletedHistoriesMap.clear()
    this._disposeChangePagination?.()
  }

  get importReasonsStore() {
    return this._importReasonsStore
  }

  addHistory = (history: History) => {
    this._historiesMap.set(history.id, history)
  }

  getImportContactProgress = async (id: number) => {
    await this.contactsImportModuleStore.getImportContactProgress(id, (history) => {
      this.addHistory(history)
    })
  }

  resetHistories = () => {
    this._historiesMap.clear()
  }

  setScrollToTOpTrigger = () => {
    this._scrollToTOpTrigger = nanoid()
  }

  loadImportHistory = async () => {
    await this.contactsImportModuleStore.loadImportHistory(
      () => {
        this.resetHistories()
        this.setScrollToTOpTrigger()
      },
      (history) => {
        this.addHistory(history)
      }
    )
  }

  deleteHistoryLocal = (id: number) => {
    this._historiesMap.delete(id)
  }

  deleteHistory = (history: History) => {
    this._deletedHistoriesMap.set(history.id, history)

    const timeoutId = setTimeout(async () => {
      await ContactsApi.deleteImportHistory(history.id)
      this._deletedHistoriesMap.delete(history.id)
      this.contactsImportModuleStore.setTotal(this.totalHistory - 1)
    }, 5000)

    this.deleteHistoryLocal(history.id)

    const toastId = nanoid()

    toastStore.add({
      id: toastId,
      title: 'Import record deleted',
      type: 'info',
      action: {
        text: 'Undo',
        onAction: () => {
          clearTimeout(timeoutId)
          const deletedHistory = this._deletedHistoriesMap.get(history.id)
          if (deletedHistory) {
            this.addHistory(deletedHistory)

            const sortedHistories = this.historyData.sort((a, b) => {
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            })

            this._historiesMap = new Map(sortedHistories.map((history) => [history.id, history]))
            this._deletedHistoriesMap.delete(history.id)
            toastStore.remove(toastId)
          }
        },
      },
    })
  }

  handleClose = () => {
    modalStore.removeModal(this._modalId)
  }

  openModal = (props: IIModalProps) => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: true,
      ...props,
    })
  }

  openDuplicateModal = async (id: number) => {
    this._modalId = 'duplicate_modal'
    this._importReasonsStore.reset()

    this.openModal({
      title: 'Duplicate numbers',
      width: 520,
      ModalContent: () => <HistoryModalContent store={this._importReasonsStore} />,
    })

    await this._importReasonsStore.load(id, 'duplicate')
  }

  openSkippedModal = async (id: number) => {
    this._modalId = 'skipped_modal'
    this._importReasonsStore.reset()

    this.openModal({
      title: 'Skipped numbers',
      width: 520,
      ModalContent: () => <HistoryModalContent store={this._importReasonsStore} />,
    })

    await this._importReasonsStore.load(id, 'skipped')
  }

  changePagination = (page: number, limit: number) => {
    this.contactsImportModuleStore.changePagination(page, limit)
  }

  reactionChangePagination = () => {
    this._disposeChangePagination?.()
    this._disposeChangePagination = reaction(
      () => this.contactsImportModuleStore.paginationData,
      () => {
        this.loadImportHistory()
      }
    )
  }
}
